(() => {
  'use strict';

  if (window.VexxCartDrawerAjaxRemoveInstalled) return;
  window.VexxCartDrawerAjaxRemoveInstalled = true;

  const ROOT_URL = (() => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  })();

  const SELECTORS = {
    drawer: '#cart-drawer',
    overlay: '#cart-drawer-overlay',
    body: '.cart-drawer__body',
    foot: '.cart-drawer__foot',
    remove: '#cart-drawer .cart-drawer__remove',
    badge: '[data-open-cart] .badge',
    openButton: '[data-open-cart]',
    header: '#site-header',
  };

  const getToastRegion = () => {
    let region = document.getElementById('vexx-add-to-cart-toast-region');

    if (region) return region;

    region = document.createElement('div');
    region.id = 'vexx-add-to-cart-toast-region';
    region.className = 'vexx-cart-toast-region';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);

    return region;
  };

  const showToast = ({ type = 'success', title, message }) => {
    const region = getToastRegion();
    region.replaceChildren();

    const toast = document.createElement('div');
    toast.className = `vexx-cart-toast vexx-cart-toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    const icon = document.createElement('span');
    icon.className = 'vexx-cart-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = type === 'error' ? '!' : '✓';

    const copy = document.createElement('div');
    copy.className = 'vexx-cart-toast__copy';

    const heading = document.createElement('strong');
    heading.className = 'vexx-cart-toast__title';
    heading.textContent = title;

    const body = document.createElement('span');
    body.className = 'vexx-cart-toast__message';
    body.textContent = message;

    const close = document.createElement('button');
    close.className = 'vexx-cart-toast__close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.textContent = '×';

    copy.append(heading, body);
    toast.append(icon, copy, close);
    region.appendChild(toast);

    const dismiss = () => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    };

    close.addEventListener('click', dismiss, { once: true });

    window.requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    window.setTimeout(dismiss, type === 'error' ? 5200 : 3200);
  };

  const updateCartBadges = (itemCount) => {
    const count = Math.max(0, Number(itemCount) || 0);

    document.querySelectorAll(SELECTORS.badge).forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count <= 0;

      badge.classList.remove('vexx-cart-badge-bump');
      void badge.offsetWidth;
      badge.classList.add('vexx-cart-badge-bump');

      window.setTimeout(() => {
        badge.classList.remove('vexx-cart-badge-bump');
      }, 450);
    });
  };

  const keepCartDrawerOpen = () => {
    const drawer = document.querySelector(SELECTORS.drawer);
    const overlay = document.querySelector(SELECTORS.overlay);
    const header = document.querySelector(SELECTORS.header);

    drawer?.classList.add('open');
    drawer?.setAttribute('aria-hidden', 'false');

    overlay?.classList.add('open');
    overlay?.setAttribute('aria-hidden', 'false');

    header?.classList.add('has-open-drawer');

    document.querySelectorAll(SELECTORS.openButton).forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });

    document.documentElement.classList.add('drawer-scroll-locked');
    document.body?.classList.add('drawer-scroll-locked');
  };

  const getHeaderSectionId = () => {
    const header = document.querySelector(SELECTORS.header);
    const wrapper = header?.closest('[id^="shopify-section-"]');

    return wrapper?.id?.replace(/^shopify-section-/, '') || '';
  };

  const fetchFreshCartDrawer = async () => {
    const sectionId = getHeaderSectionId();

    if (!sectionId) {
      throw new Error('The cart drawer section could not be identified.');
    }

    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set('sections', sectionId);

    const response = await fetch(sectionUrl.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('The cart drawer could not be refreshed.');
    }

    const sections = await response.json();
    const freshHeaderHtml = sections?.[sectionId];

    if (!freshHeaderHtml) {
      throw new Error('The refreshed cart drawer was empty.');
    }

    const parsed = new DOMParser().parseFromString(freshHeaderHtml, 'text/html');
    const freshDrawer = parsed.querySelector(SELECTORS.drawer);

    if (!freshDrawer) {
      throw new Error('The refreshed cart drawer was not found.');
    }

    return freshDrawer;
  };

  const replaceCartDrawerContents = (freshDrawer, previousScrollTop = 0) => {
    const currentDrawer = document.querySelector(SELECTORS.drawer);

    if (!currentDrawer) {
      throw new Error('The current cart drawer was not found.');
    }

    const freshBody = freshDrawer.querySelector(SELECTORS.body);
    const currentBody = currentDrawer.querySelector(SELECTORS.body);

    if (freshBody && currentBody) {
      currentBody.replaceWith(freshBody.cloneNode(true));
    }

    const freshFoot = freshDrawer.querySelector(SELECTORS.foot);
    const currentFoot = currentDrawer.querySelector(SELECTORS.foot);

    if (freshFoot && currentFoot) {
      currentFoot.replaceWith(freshFoot.cloneNode(true));
    } else if (freshFoot && !currentFoot) {
      currentDrawer.appendChild(freshFoot.cloneNode(true));
    } else if (!freshFoot && currentFoot) {
      currentFoot.remove();
    }

    const refreshedBody = currentDrawer.querySelector(SELECTORS.body);

    if (refreshedBody) {
      window.requestAnimationFrame(() => {
        const maximumScroll = Math.max(
          0,
          refreshedBody.scrollHeight - refreshedBody.clientHeight
        );

        refreshedBody.scrollTop = Math.min(previousScrollTop, maximumScroll);
      });
    }

    keepCartDrawerOpen();
  };

  const refreshCartInterface = async (previousScrollTop = 0) => {
    const [cartResponse, freshDrawer] = await Promise.all([
      fetch(`${ROOT_URL}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      }),
      fetchFreshCartDrawer(),
    ]);

    if (!cartResponse.ok) {
      throw new Error('The bag could not be refreshed.');
    }

    const cart = await cartResponse.json();

    updateCartBadges(cart.item_count);
    replaceCartDrawerContents(freshDrawer, previousScrollTop);

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart },
      })
    );

    /*
     * Some theme scripts synchronize drawer state after cart updates.
     * Reassert the open state after that event without touching the X handler.
     */
    window.requestAnimationFrame(keepCartDrawerOpen);
    window.setTimeout(keepCartDrawerOpen, 60);

    return cart;
  };

  const parseRemoveRequest = (link) => {
    const removeUrl = new URL(link.href, window.location.origin);
    const lineValue = Number(removeUrl.searchParams.get('line'));
    const itemId = removeUrl.searchParams.get('id');

    if (Number.isInteger(lineValue) && lineValue > 0) {
      return {
        line: lineValue,
        quantity: 0,
      };
    }

    if (itemId) {
      return {
        id: itemId,
        quantity: 0,
      };
    }

    return null;
  };

  const parseCartError = async (response) => {
    try {
      const payload = await response.json();

      return (
        payload?.description ||
        payload?.message ||
        'This item could not be removed.'
      );
    } catch (error) {
      return 'This item could not be removed. Please try again.';
    }
  };

  const removeCartItem = async (link) => {
    if (link.dataset.removing === 'true') return;

    const requestBody = parseRemoveRequest(link);

    if (!requestBody) {
      throw new Error('The item position could not be identified.');
    }

    const drawerBody = link.closest(SELECTORS.body);
    const previousScrollTop = drawerBody?.scrollTop || 0;
    const productTitle =
      link
        .closest('.cart-drawer__item')
        ?.querySelector('.cart-drawer__title')
        ?.textContent
        ?.trim() || 'The item';

    const originalText = link.textContent;
    link.dataset.removing = 'true';
    link.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');
    link.style.pointerEvents = 'none';
    link.textContent = 'Removing…';

    try {
      const response = await fetch(`${ROOT_URL}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(await parseCartError(response));
      }

      await response.json();
      const cart = await refreshCartInterface(previousScrollTop);

      showToast({
        type: 'success',
        title: 'REMOVED FROM YOUR BAG',
        message:
          cart.item_count > 0
            ? `${productTitle} was removed. ${cart.item_count} item${cart.item_count === 1 ? '' : 's'} remain in your bag.`
            : `${productTitle} was removed. Your bag is now empty.`,
      });
    } catch (error) {
      link.dataset.removing = 'false';
      link.removeAttribute('aria-busy');
      link.removeAttribute('aria-disabled');
      link.style.removeProperty('pointer-events');
      link.textContent = originalText;

      showToast({
        type: 'error',
        title: 'COULD NOT REMOVE ITEM',
        message:
          error instanceof Error
            ? error.message
            : 'Please try again.',
      });

      throw error;
    }
  };

  /*
   * Capture phase prevents the normal remove link from navigating to /cart.
   * Event delegation also continues working after the drawer HTML refreshes.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element ? event.target : null;

      const removeLink = target?.closest(SELECTORS.remove);

      if (!(removeLink instanceof HTMLAnchorElement)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      removeCartItem(removeLink).catch((error) => {
        console.error('VEXX cart remove error:', error);
      });
    },
    true
  );
})();
